import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import { PushNotifications } from '@capacitor/push-notifications'
import { get, post } from './api'
import { t } from './i18n'
import type { PushState } from './push'

const available = () => Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable('PushNotifications')
let registeredUser: string | null = null
let pending: Promise<PushState> | null = null
let generation = 0

function installId(): string {
  let id = localStorage.getItem('nativePushDeviceId')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('nativePushDeviceId', id)
  }
  return id
}

export async function nativePushState(): Promise<PushState> {
  if (!available()) return 'unsupported'
  const permissions = await PushNotifications.checkPermissions()
  if (permissions.receive === 'denied') return 'blocked'
  const { user } = await get<{ user: { id: string } | null }>('/api/auth/me')
  return user && registeredUser === user.id && permissions.receive === 'granted' ? 'on' : 'off'
}

async function register(label?: string): Promise<PushState> {
  const current = generation
  const { user } = await get<{ user: { id: string } | null }>('/api/auth/me')
  if (!user) return 'off'
  const handles: PluginListenerHandle[] = []
  let resolveToken: (token: string) => void = () => {}
  let rejectToken: (error: Error) => void = () => {}
  const token = new Promise<string>((resolve, reject) => { resolveToken = resolve; rejectToken = reject })
  // Install both callbacks BEFORE asking APNs for a token. No tokens enter logs.
  handles.push(await PushNotifications.addListener('registration', (event) => resolveToken(event.value)))
  handles.push(await PushNotifications.addListener('registrationError', () => rejectToken(new Error(t('notify.failed')))))
  const timeout = setTimeout(() => rejectToken(new Error(t('notify.failed'))), 15000)
  try {
    // Observe rejection immediately, even if register() itself fails first.
    const registering = PushNotifications.register()
    const [endpoint] = await Promise.all([token, registering])
    if (current !== generation) return 'off'
    const result = await post<{ deliveryReady: boolean }>('/api/native-push/register', { endpoint, deviceId: installId(), userId: user.id, label })
    if (!result.deliveryReady) throw new Error(t('notify.failed'))
    if (current !== generation) return 'off'
    registeredUser = user.id
    return 'on'
  } finally {
    clearTimeout(timeout)
    await Promise.all(handles.map((handle) => handle.remove()))
  }
}

function reconnect(label?: string): Promise<PushState> {
  pending ??= register(label).finally(() => { pending = null })
  return pending
}

export async function enableNativePush(label?: string): Promise<PushState> {
  if (!available()) return 'unsupported'
  const permissions = await PushNotifications.requestPermissions()
  if (permissions.receive !== 'granted') return permissions.receive === 'denied' ? 'blocked' : 'off'
  return reconnect(label)
}

/** Reconnect existing permission on sign-in/resume; never prompt automatically. */
export function restoreNativePush(userId: string): () => void {
  if (!available()) return () => {}
  let stopped = false
  const handles: PluginListenerHandle[] = []
  const refresh = async () => {
    if (stopped || (await PushNotifications.checkPermissions()).receive !== 'granted') return
    await reconnect()
  }
  void refresh().catch(() => { registeredUser = null })
  if (Capacitor.isPluginAvailable('App')) {
    void App.addListener('appStateChange', (event) => {
      if (event.isActive) void refresh().catch(() => { registeredUser = null })
    }).then((handle) => { if (stopped) void handle.remove(); else handles.push(handle) })
  }
  void PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
    // Accept only same-origin recipe links, never a provider-supplied external URL.
    const target = event.notification.data?.target
    if (typeof target !== 'string') return
    const url = new URL(target, location.origin)
    if (url.origin !== location.origin || url.pathname !== '/') return
    const recipe = url.searchParams.get('recipe')
    if (recipe) location.assign(`/?recipe=${encodeURIComponent(recipe)}`)
    else location.assign('/')
  }).then((handle) => { if (stopped) void handle.remove(); else handles.push(handle) })
  return () => {
    stopped = true
    if (registeredUser === userId) registeredUser = null
    generation++
    void Promise.all(handles.map((handle) => handle.remove()))
  }
}

export async function releaseNativePush(): Promise<void> {
  if (!available()) return
  generation++
  // Finish any already-authorized registration before revocation, preventing a
  // late callback from reactivating the installation after sign-out.
  await pending?.catch(() => {})
  await post('/api/native-push/revoke', { deviceId: installId() })
  await PushNotifications.unregister()
  registeredUser = null
}
